import React, { useState } from 'react';
import {
  Box,
  Backdrop,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AlternateEmail as AlternateEmailIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuthStore } from '../../store/authStore';
import { AuthService } from '../../services/authService';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';

interface RegisterFormProps {
  onSuccess: () => void;
}

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  alias: string;
}

const registerSchema = yup.object({
  firstName: yup
    .string()
    .min(2, 'First name must be at least 2 characters')
    .required('First name is required'),
  lastName: yup
    .string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  alias: yup
    .string()
    .min(2, 'Alias must be at least 2 characters')
    .max(50, 'Alias must be at most 50 characters')
    .matches(/^[a-zA-Z0-9_]+$/, 'Alias can only contain letters, numbers, and underscores')
    .required('Alias is required'),
});

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { login } = useAuthStore();
  const { t } = useTranslation();
  const { settings } = useWebsiteSettings();
  const platformName = (settings as any)?.branding?.platformName || 'FashionHub';

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setSubmitError(null);
    clearErrors('alias');
    try {
      // Register with Supabase (regular user registration)
      const { user } = await AuthService.signUp(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        false, // isAdmin = false for regular user registration
        'consumer', // Default preference
        data.alias // Pass alias
      );

      // Transform user data for auth store
      const authUser = {
        id: user.id,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        firstName: user.firstName,
        lastName: user.lastName,
        role: 'buyer' as const, // Default role
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isAdmin: false,
        userStatusPreference: user.userStatusPreference,
      };

      login(authUser);
      toast.success(`Welcome to ${platformName}! Your account has been created successfully.`);
      onSuccess();
    } catch (error: any) {
      console.error('Registration error:', error);
      const rawMsg = String(error?.message || '');
      const isAliasTaken = rawMsg.includes('users_alias_key') || rawMsg.toLowerCase().includes('alias already exists');
      if (isAliasTaken) {
        const msg = 'This alias already exists. Please choose a different one.';
        setSubmitError(msg);
        setError('alias', { type: 'manual', message: msg });
        toast.error(msg);
        return;
      }
      setSubmitError(rawMsg || 'Registration failed. Please try again.');
      toast.error(rawMsg || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ px: 3, position: 'relative' }}>
      <Backdrop
        open={isLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(17, 24, 39, 0.45)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2.5,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, rgba(17,24,39,0.28) 100%)`,
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            minWidth: 280,
          }}
        >
          <CircularProgress
            size={44}
            thickness={4}
            sx={{
              color: theme.palette.primary.light,
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
            }}
          />
          <Typography sx={{ fontWeight: 800, letterSpacing: 0.2 }}>Creating your account…</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center' }}>
            This can take a few seconds.
          </Typography>
        </Box>
      </Backdrop>

      {submitError && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 3,
            border: '1px solid rgba(239,68,68,0.25)',
            background: 'linear-gradient(180deg, rgba(239,68,68,0.10) 0%, rgba(239,68,68,0.04) 100%)',
            color: '#7f1d1d',
            '& .MuiAlert-icon': { color: '#ef4444' },
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 0.25 }}>We couldn’t create your account</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>{submitError}</Typography>
        </Alert>
      )}

      {/* Name Fields */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          {...register('firstName')}
          fullWidth
          label={t('auth.firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          {...register('lastName')}
          fullWidth
          label={t('auth.lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
          disabled={isLoading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      {/* Email Field */}
      <TextField
        {...register('email')}
        fullWidth
        label={t('auth.email')}
        type="email"
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Password Field */}
      <TextField
        {...register('password')}
        fullWidth
        label={t('auth.password')}
        type={showPassword ? 'text' : 'password'}
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Confirm Password Field */}
      <TextField
        {...register('confirmPassword')}
        fullWidth
        label={t('auth.confirmPassword')}
        type={showConfirmPassword ? 'text' : 'password'}
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                edge="end"
              >
                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Alias Field */}
      <TextField
        {...register('alias')}
        fullWidth
        label={t('auth.alias')}
        placeholder={t('auth.alias.placeholder')}
        error={!!errors.alias}
        helperText={errors.alias?.message || t('auth.alias.helper')}
        disabled={isLoading}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <AlternateEmailIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Submit Button */}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={isLoading}
        sx={{
          py: 1.5,
          mt: 1,
          fontSize: '1rem',
          fontWeight: 700,
          borderRadius: 999,
          textTransform: 'none',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.32)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.42)}`,
          },
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CircularProgress
              size={18}
              thickness={5}
              sx={{
                color: 'rgba(255,255,255,0.92)',
                '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
              }}
            />
            <span>{t('auth.creatingAccount')}</span>
          </Box>
        ) : (
          t('auth.createAccount')
        )}
      </Button>

      {/* Sign In Link */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link
            href="#"
            variant="body2"
            color="primary.main"
            sx={{ textDecoration: 'none', fontWeight: 600 }}
            onClick={(e) => e.preventDefault()}
          >
            {t('auth.signInHere')}
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default RegisterForm;
